import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isAuthorizedClinicianRole } from '../lib/clinicianRoles';
import { emailConfirmRedirectUrl } from '../lib/authContinue';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Demo credentials — Investor/demo mode only
const DEMO_EMAIL = 'demo@curegate.ai';
const DEMO_PASSWORD = 'Demo@2026';

// Production superadmin — always routes to live environment
const PRODUCTION_SUPERADMIN_EMAIL = 'abeer@curegate.ai';

// ── Investor/Demo Dashboard route — the ONLY route demo users can access ──────
export const DEMO_ALLOWED_ROUTES = ['/investor-dashboard', '/'];

// ── Roles that are exempt from email verification enforcement ─────────────────
const VERIFICATION_EXEMPT_ROLES = ['superadmin', 'superuser', 'staff', 'site_clinic', 'clinic', 'sponsor_cro', 'sponsor', 'investor_demo', 'investor', 'clincore', 'fellow', 'nurse', 'nurse_reviewer', 'coordinator', 'provider_clinician', 'clinician_reviewer', 'management', 'operations', 'admin'];

// ── Auth-provider email confirmation (Supabase Auth) ─────────────────────────
// Only trust email_confirmed_at / confirmed_at from the session object.
const isAuthEmailConfirmed = (supabaseUser) => {
  if (!supabaseUser) return false;
  return !!(supabaseUser?.email_confirmed_at || supabaseUser?.confirmed_at);
};

// ── App-level patient verification (survives mailer_autoconfirm=true) ────────
// When Supabase Auth auto-confirms emails, email_confirmed_at is always set on
// signup. Production gating MUST use user_profiles.email_verified /
// verification_status, which default to false/'unverified' and are only flipped
// by admin_mark_email_verified or confirm_patient_email_verified.
export const isPatientAppVerified = (profile) => {
  if (!profile) return false;
  return (
    profile?.email_verified === true ||
    profile?.verification_status === 'verified'
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [isDemoMode, setIsDemoMode] = useState(false)

  const loadProfile = async (userId) => {
    if (!userId) return
    setProfileLoading(true)
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
      if (!error && data) setUserProfile(data)
    } catch (err) {
      console.error('Profile load error:', err)
    } finally {
      setProfileLoading(false)
    }
  }

  const clearProfile = () => {
    setUserProfile(null)
    setProfileLoading(false)
  }

  // ── Full session purge — called on logout AND role switching ─────────────────
  // Guarantees no stale admin email or admin context leaks into unverified-account,
  // guest upload notices, or any other screen that reads from AuthContext.
  const purgeAllSessionState = () => {
    setUser(null)
    setUserProfile(null)
    setIsDemoMode(false)
    setProfileLoading(false)
    sessionStorage.removeItem('curegate_demo_mode')
    localStorage.removeItem('curegate_demo_mode')
    // Purge any role-switch context that may have been cached
    sessionStorage.removeItem('curegate_active_role')
    localStorage.removeItem('curegate_active_role')
  }

  // ── Session restore gate ──────────────────────────────────────────────────
  // Staff/admin roles are exempt. Patients may keep a session even when
  // app-level verification is pending (so /unverified-account can resend),
  // but only if Auth email is confirmed OR autoconfirm is in use.
  // Hard-sign-out only when Auth itself reports unconfirmed email.
  const enforceVerificationOnRestore = async (sessionUser) => {
    if (!sessionUser) return false;

    const role =
      sessionUser?.app_metadata?.role ||
      sessionUser?.user_metadata?.role;
    if (role && VERIFICATION_EXEMPT_ROLES?.includes(role)) return true;

    // Prefer app_metadata role; fall back to profile after load.
    // Patients keep a session even when email confirmation is still pending.
    // The dashboard shows a banner instead of a lockout wall.
    if (!isAuthEmailConfirmed(sessionUser)) {
      const patientSession = !role || role === 'patient';
      if (patientSession) return true;
      console.warn('[AuthContext] Auth-unconfirmed session — signing out:', sessionUser?.email);
      await supabase?.auth?.signOut();
      return false;
    }
    // App-level verification is enforced by ProtectedRoute via userProfile.
    return true;
  };

  useEffect(() => {
    // Restore demo/investor mode from sessionStorage on page reload
    let savedDemo = null
    try { savedDemo = sessionStorage.getItem('curegate_demo_mode') } catch { /* storage blocked (e.g. Google renderer) */ }
    if (savedDemo === 'true') {
      setIsDemoMode(true)
      setUser({ id: 'demo', email: DEMO_EMAIL, user_metadata: { role: 'investor_demo', full_name: 'Demo Investor' } })
      setUserProfile({ id: 'demo', email: DEMO_EMAIL, full_name: 'Demo Investor', role: 'investor_demo', permissions: [], subscriptions: [] })
      setLoading(false)
      return
    }

    supabase?.auth?.getSession()?.then(async ({ data: { session } }) => {
      if (session?.user) {
        const allowed = await enforceVerificationOnRestore(session?.user);
        if (allowed) {
          setUser(session?.user);
          loadProfile(session?.user?.id);
        } else {
          // Unverified — session was signed out; redirect handled by ProtectedRoute
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          // On SIGNED_IN events, enforce verification gate
          if (event === 'SIGNED_IN') {
            const allowed = await enforceVerificationOnRestore(session?.user);
            if (allowed) {
              setUser(session?.user);
              loadProfile(session?.user?.id);
            } else {
              setUser(null);
              clearProfile();
            }
          } else {
            // TOKEN_REFRESHED, USER_UPDATED, etc. — trust existing session
            setUser(session?.user);
            loadProfile(session?.user?.id);
          }
        } else {
          setUser(null);
          clearProfile();
        }
        setLoading(false);
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    // Intercept demo credentials — route to Investor/demo sandbox ONLY
    if (email?.toLowerCase() === DEMO_EMAIL && password === DEMO_PASSWORD) {
      const demoUser = { id: 'demo', email: DEMO_EMAIL, user_metadata: { role: 'investor_demo', full_name: 'Demo Investor' } }
      const demoProfile = { id: 'demo', email: DEMO_EMAIL, full_name: 'Demo Investor', role: 'investor_demo', permissions: [], subscriptions: [] }
      setUser(demoUser)
      setUserProfile(demoProfile)
      setIsDemoMode(true)
      sessionStorage.setItem('curegate_demo_mode', 'true')
      // Demo users ONLY go to investor-dashboard — never admin portal
      return { data: { user: demoUser, redirectTo: '/investor-dashboard' }, error: null }
    }

    // Block demo@curegate.ai from using any other password (strict demo isolation)
    if (email?.toLowerCase() === DEMO_EMAIL) {
      return { data: null, error: { message: 'This account is restricted to the demo environment. Please use the designated demo credentials.' } }
    }

    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      if (!error && data?.user) {
        // Auth-level unconfirmed → hard reject (when autoconfirm is disabled)
        if (!isAuthEmailConfirmed(data?.user)) {
          await supabase?.auth?.signOut()
          setUser(null)
          clearProfile()
          return {
            data: null,
            error: {
              message: 'Email not confirmed',
              code: 'email_not_confirmed',
              email: data?.user?.email,
            }
          }
        }

        setIsDemoMode(false)
        sessionStorage.removeItem('curegate_demo_mode')
        localStorage.removeItem('curegate_demo_mode')
        await loadProfile(data?.user?.id)

        // App-level patient gate (works even when mailer_autoconfirm=true).
        // Keep the session so /unverified-account can resend; block dashboard
        // via ProtectedRoute + this error code for login redirects.
        const role =
          data?.user?.app_metadata?.role ||
          data?.user?.user_metadata?.role ||
          'patient'
        if (!VERIFICATION_EXEMPT_ROLES?.includes(role)) {
          const { data: profileRow } = await supabase
            ?.from('user_profiles')
            ?.select('email_verified, verification_status, role')
            ?.eq('id', data?.user?.id)
            ?.maybeSingle()

          const profileRole = profileRow?.role || role
          if (!VERIFICATION_EXEMPT_ROLES?.includes(profileRole) && !isPatientAppVerified(profileRow)) {
            return {
              data: { user: data?.user, session: data?.session },
              error: {
                message: 'Email not confirmed',
                code: 'email_not_confirmed',
                email: data?.user?.email,
              }
            }
          }
        }

        if (email?.toLowerCase() === PRODUCTION_SUPERADMIN_EMAIL) {
          sessionStorage.removeItem('curegate_demo_mode')
          localStorage.removeItem('curegate_demo_mode')
          setIsDemoMode(false)
        }
      }
      return { data, error }
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://curegate.ai';
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: emailConfirmRedirectUrl(origin),
          data: { role: 'patient', post_verify_path: '/patient-dashboard', ...metadata },
        }
      })
      return { data, error }
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    // Clear demo/investor mode
    if (isDemoMode) {
      purgeAllSessionState()
      return { error: null }
    }
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        purgeAllSessionState()
      }
      return { error }
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    if (isDemoMode) return { error: { message: 'Profile updates are not available in demo mode.' } }
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // ── Role helpers (consolidated 7-role system) ──────────────────────────────
  const userRole = userProfile?.role || user?.user_metadata?.role || null

  // P0-04: Normalize role checks — accept both `role` and `role_alias` as super_admin.
  // Some tokens may carry role_alias instead of role in app_metadata.
  const roleAlias = user?.app_metadata?.role_alias || user?.user_metadata?.role_alias || null
  const effectiveRole = userRole || roleAlias || null

  const userPermissions = userProfile?.permissions || []

  // Superadmin: full access — accepts role === 'super_admin' OR role_alias === 'super_admin'
  const isSuperuser =
    effectiveRole === 'superadmin' ||
    effectiveRole === 'superuser' ||
    effectiveRole === 'super_admin' ||
    roleAlias === 'super_admin' ||
    roleAlias === 'superadmin'

  // Investor/demo
  const isInvestor = effectiveRole === 'investor_demo' || effectiveRole === 'investor' || isDemoMode

  // Staff: single role with permission-based access
  const isStaff = effectiveRole === 'staff' || isSuperuser

  // Clinician review: any authorized clinician role (not sponsor/site/patient)
  const isClinician = isStaff || isAuthorizedClinicianRole(effectiveRole)

  // External customer roles
  const isClinic = effectiveRole === 'site_clinic' || effectiveRole === 'clinic'
  const isSponsor = effectiveRole === 'sponsor_cro' || effectiveRole === 'sponsor'
  const isPatient = effectiveRole === 'patient'
  const isClincore = effectiveRole === 'clincore' || effectiveRole === 'fellow'
  const isEmailVerified =
    isDemoMode ||
    VERIFICATION_EXEMPT_ROLES?.includes(effectiveRole) ||
    isPatientAppVerified(userProfile)

  // Permission helpers for staff
  const hasPermission = (perm) => {
    if (isSuperuser) return true // superadmin has all permissions
    return userPermissions?.includes(perm)
  }

  // Dual-mode: investor/demo → sandbox; all others → production
  // abeer@curegate.ai always gets production mode regardless
  const isProductionMode = !!user && !isDemoMode && !isInvestor

  // P0 Sprint 1: Centralized identity context for feature banners
  // Always derive display identity from the ACTIVE session — never from stale state.
  // Priority: userProfile (DB) > session user_metadata > session email
  // This prevents stale admin email states from leaking into feature verification banners.
  const activeSessionEmail = isDemoMode
    ? DEMO_EMAIL
    : (userProfile?.email || user?.email || null)

  const activeSessionRole = isDemoMode
    ? 'investor_demo'
    : effectiveRole

  // Friendly display name for the logged-in user
  // Always derived from the current session — never cached from a previous session
  const displayName = isDemoMode
    ? 'Demo Investor'
    : (
        userProfile?.full_name
          ? userProfile?.full_name?.split(' ')?.[0]
          : (user?.user_metadata?.full_name
              ? user?.user_metadata?.full_name?.split(' ')?.[0]
              : null)
      )

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    isDemoMode,
    isProductionMode,
    displayName,
    // P0 Sprint 1: Centralized identity — use these in feature banners instead of raw user.email
    activeSessionEmail,
    activeSessionRole,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile: () => loadProfile(user?.id),
    isAuthenticated: !!user,
    userRole: effectiveRole,
    userPermissions,
    isSuperuser,
    isInvestor,
    isStaff,
    isClinician,
    isClinic,
    isSponsor,
    isPatient,
    isClincore,
    isEmailVerified,
    hasPermission,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
